import cx from 'classnames'

import Styles from './StyleSection.module.scss'
import { ArticleStyle, ArticleStyleProperty } from 'types'
import { ColorPicker } from 'common/ColorPicker/ColorPicker'
import { DropdownInput } from 'common/DropdownInput/DropdownInput'
import { DropdownSelect } from 'common/DropdownSelect/DropdownSelect'
import {
  ColorOptions,
  FontOptions,
  FontSizeOptions,
  FontStyleOptions,
  FontWeightOptions,
  LineHeightOptions,
  PaddingOptions,
  TextAlignOptions,
  TextDecorationOptions,
  TextTransformOptions,
} from './constants'
import { ReactComponent as NoColorIcon } from '../noColor.svg'

// NOTE: parses a css px value into a number for consumption by the `value` prop of a number input - phew!

const getPxValue = (propertyValue: string | undefined | null, defaultValue: string): number | '' => {
  // TODO: Fix this the next time the file is edited.
  /* eslint-disable no-nested-ternary */
  return propertyValue === undefined || propertyValue === null
    ? parseFloat(defaultValue)
    : propertyValue.length
    ? parseFloat(propertyValue)
    : ''
  /* eslint-enable no-nested-ternary */
}

interface Props {
  defaults: Required<ArticleStyleProperty>
  name: keyof ArticleStyle
  property: ArticleStyleProperty
  setFieldValue: (name: keyof ArticleStyle, value: ArticleStyleProperty) => void
}

export const StyleSectionForm = ({ defaults, name, property, setFieldValue }: Props) => {
  const setPxValue = (name: string, value: string | number) => handlePropertyKeyChange(name, value ? `${value}px` : '')

  const handlePropertyKeyChange = (propertyKey: string, value: string | number | null) => {
    const newValues = { ...property, [propertyKey]: value }

    // if we are setting a value to its default, just unset it (useful for
    // change detection in the form)
    if (value === null || value === defaults[propertyKey as keyof typeof defaults])
      delete newValues[propertyKey as keyof typeof newValues]

    setFieldValue(name, newValues)
  }

  return (
    <>
      <div className={Styles.setting}>
        Font
        <DropdownSelect
          className={Styles.control}
          dropdownClassName={cx(Styles.controlDropdown, Styles.fontDropdown)}
          name="font_family"
          options={FontOptions}
          setFieldValue={handlePropertyKeyChange}
          value={property.font_family || defaults.font_family}
        />
      </div>
      <div className={Styles.setting}>
        Size (px)
        <DropdownInput
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          inputProps={{ max: 999, min: 1, required: true, step: 1, type: 'number' }}
          name="font_size"
          options={FontSizeOptions}
          setFieldValue={setPxValue}
          value={getPxValue(property.font_size, defaults.font_size)}
        />
      </div>
      <div className={Styles.setting}>
        Style
        <DropdownSelect
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          name="font_style"
          options={FontStyleOptions}
          setFieldValue={handlePropertyKeyChange}
          value={property.font_style || defaults.font_style}
        />
      </div>
      <div className={Styles.setting}>
        Weight
        <DropdownSelect
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          name="font_weight"
          options={FontWeightOptions(property.font_family || defaults.font_family)}
          setFieldValue={handlePropertyKeyChange}
          value={property.font_weight || defaults.font_weight}
        />
      </div>
      <div className={Styles.setting}>
        Text Decoration
        <DropdownSelect
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          name="text_decoration"
          options={TextDecorationOptions}
          setFieldValue={handlePropertyKeyChange}
          value={property.text_decoration || defaults.text_decoration}
        />
      </div>
      <div className={Styles.setting}>
        Color
        <div className={Styles.control}>
          {property.color && (
            <button className={Styles.removeColor} onClick={() => handlePropertyKeyChange('color', null)} type="button">
              Remove color
            </button>
          )}
          <ColorPicker
            ariaLabel="Select color"
            color={property.color || defaults.color}
            colors={ColorOptions}
            onChange={(color) => handlePropertyKeyChange('color', color)}
          >
            <div className={Styles.colorPreview} style={{ backgroundColor: property.color || defaults.color }} />
          </ColorPicker>
        </div>
      </div>
      <div className={Styles.setting}>
        Background Color
        <div className={Styles.control}>
          {property.background_color && (
            <button
              className={Styles.removeColor}
              onClick={() => handlePropertyKeyChange('background_color', null)}
              type="button"
            >
              Remove color
            </button>
          )}
          <ColorPicker
            ariaLabel="Select background_color"
            color={property.background_color || defaults.background_color}
            colors={ColorOptions}
            onChange={(color) => handlePropertyKeyChange('background_color', color)}
          >
            {
              // TODO: Fix this the next time the file is edited.
              /* eslint-disable no-nested-ternary */
              property.background_color ? (
                <div className={Styles.colorPreview} style={{ backgroundColor: property.background_color }} />
              ) : defaults.background_color ? (
                <div className={Styles.colorPreview} style={{ backgroundColor: defaults.background_color }} />
              ) : (
                <NoColorIcon className={Styles.noColor} />
              )
              /* eslint-enable no-nested-ternary */
            }
          </ColorPicker>
        </div>
      </div>
      <div className={Styles.setting}>
        Alignment
        <DropdownSelect
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          name="text_align"
          options={TextAlignOptions}
          setFieldValue={handlePropertyKeyChange}
          value={property.text_align || defaults.text_align}
        />
      </div>
      <div className={Styles.setting}>
        Line Height
        <DropdownInput
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          inputProps={{ max: 10, min: 0.01, required: true, step: 0.01, type: 'number' }}
          name="line_height"
          options={LineHeightOptions}
          setFieldValue={handlePropertyKeyChange}
          value={
            property.line_height === undefined || property.line_height === null
              ? defaults.line_height
              : property.line_height
          }
        />
      </div>
      <div className={Styles.setting}>
        Padding Above (px)
        <DropdownInput
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          inputProps={{ max: 999, min: 0, required: true, step: 1, type: 'number' }}
          name="padding_top"
          options={PaddingOptions}
          setFieldValue={setPxValue}
          value={getPxValue(property.padding_top, defaults.padding_top)}
        />
      </div>
      <div className={Styles.setting}>
        Padding Below (px)
        <DropdownInput
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          inputProps={{ max: 999, min: 0, required: true, step: 1, type: 'number' }}
          name="padding_bottom"
          options={PaddingOptions}
          setFieldValue={setPxValue}
          value={getPxValue(property.padding_bottom, defaults.padding_bottom)}
        />
      </div>
      <div className={Styles.setting}>
        Case
        <DropdownSelect
          className={Styles.control}
          dropdownClassName={Styles.controlDropdown}
          name="text_transform"
          options={TextTransformOptions}
          setFieldValue={handlePropertyKeyChange}
          value={property.text_transform || defaults.text_transform}
        />
      </div>
    </>
  )
}
